import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import MotherIcon from 'src/v3/core/mother-icon'
import MotherThinking from 'src/v3/components/mother-thinking'
import {
  createSyllabus,
  generateSyllabusWithAI,
  checkSyllabusGenerationStatus,
  fetchSyllabus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'

type SyllabusCreationFormProps = {
  open: boolean
  onClose: () => void
  eventID: string
  eventName?: string
}

export default ({
  open,
  onClose,
  eventID,
  eventName,
}: SyllabusCreationFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [loading, setLoading] = useState(false)
  let [aiGenerating, setAiGenerating] = useState(false)
  let [generationProgress, setGenerationProgress] = useState<string[]>([])
  let [generationError, setGenerationError] = useState<string | null>(null)
  let [promptInput, setPromptInput] = useState('')
  let [showAiSection, setShowAiSection] = useState(false)

  let form = useForm({
    defaultValues: {
      text: '',
      practiceGap: '',
      practiceGapEvidence: '',
      learnerEngagement: '',
      learningOutcomes: '',
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(
          createSyllabus({
            eventID,
            text: value.text,
            practiceGap: value.practiceGap,
            practiceGapEvidence: value.practiceGapEvidence,
            learnerEngagement: value.learnerEngagement,
            learningOutcomes: value.learningOutcomes,
          }),
        )
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchSyllabus(eventID))
        onClose()
        form.reset()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create syllabus:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  // Start AI generation
  let handleAiGeneration = async () => {
    if (!promptInput.trim()) return

    setAiGenerating(true)
    setGenerationProgress([])
    setGenerationError(null)

    try {
      // Start generation
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      let result = await dispatch(
        generateSyllabusWithAI({
          eventID,
          prompt: promptInput.trim(),
        }),
      )

      if (
        result.payload &&
        typeof result.payload === 'object' &&
        'correlationID' in result.payload
      ) {
        let correlationId = (result.payload as any).correlationID

        // Poll for status
        let pollInterval = setInterval(async () => {
          try {
            // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
            let statusResult = await dispatch(
              checkSyllabusGenerationStatus(correlationId as string),
            )

            if (
              statusResult.payload &&
              typeof statusResult.payload === 'object' &&
              'status' in statusResult.payload
            ) {
              let {
                status,
                progress,
                result: generationResult,
              } = statusResult.payload as any

              // Update progress messages
              let progressMessages = (progress || []).map((p: any) => p.message)
              setGenerationProgress(progressMessages)

              if (status === 'SessionCompleted' && generationResult) {
                clearInterval(pollInterval)
                setAiGenerating(false)

                try {
                  // Validate required fields
                  if (!generationResult.text && !generationResult.practiceGap) {
                    setGenerationError('AI result missing content')
                    return
                  }

                  // Populate form with generated data
                  form.setFieldValue('text', generationResult.text || '')
                  form.setFieldValue(
                    'practiceGap',
                    generationResult.practiceGap || '',
                  )
                  form.setFieldValue(
                    'practiceGapEvidence',
                    generationResult.practiceGapEvidence || '',
                  )
                  form.setFieldValue(
                    'learnerEngagement',
                    generationResult.learnerEngagement || '',
                  )
                  form.setFieldValue(
                    'learningOutcomes',
                    generationResult.learningOutcomes || '',
                  )

                  setPromptInput('')
                  setShowAiSection(false)
                } catch {
                  setGenerationError(
                    'The AI generated an invalid result. Please try again with a different prompt.',
                  )
                }
              } else if (status === 'SessionFailed') {
                clearInterval(pollInterval)
                setAiGenerating(false)
                setGenerationError('AI generation failed. Please try again.')
              }
            }
          } catch {
            clearInterval(pollInterval)
            setAiGenerating(false)
            setGenerationError('Failed to check generation status')
          }
        }, 2000) // Poll every 2 seconds
      }
    } catch {
      setAiGenerating(false)
      setGenerationError('Failed to start AI generation')
    }
  }

  let handleClose = () => {
    form.reset()
    setGenerationProgress([])
    setGenerationError(null)
    setPromptInput('')
    setShowAiSection(false)
    setAiGenerating(false)
    onClose()
  }

  useEffect(() => {
    if (open && eventName) {
      setPromptInput(`Generate a comprehensive syllabus for ${eventName}`)
    }
  }, [open, eventName])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create Syllabus</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          {/* AI Generation Section */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">Generate with AI</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAiSection(!showAiSection)}
                disabled={loading}
                startIcon={!showAiSection ? <MotherIcon /> : undefined}
              >
                {showAiSection ? 'Hide Generator' : 'Generate with MOTHER'}
              </Button>
            </Box>

            {showAiSection && (
              <Box
                sx={{ p: 2, bgcolor: 'background.paperDark', borderRadius: 1 }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Describe your syllabus"
                  placeholder={`e.g., Generate a comprehensive syllabus for ${eventName || 'this course'} including practice gaps, learner engagement strategies, and learning outcomes`}
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  disabled={aiGenerating}
                  sx={{ mb: 2, bgcolor: 'background.paper' }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleAiGeneration}
                    disabled={aiGenerating || !promptInput.trim()}
                    size="small"
                  >
                    {aiGenerating ? 'Generating...' : 'Generate Syllabus'}
                  </Button>
                  {aiGenerating && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAiGenerating(false)
                        setGenerationProgress([])
                      }}
                      size="small"
                    >
                      Cancel
                    </Button>
                  )}
                </Box>

                {aiGenerating && (
                  <Box sx={{ mb: 2 }}>
                    <MotherThinking
                      isThinking={aiGenerating}
                      currentMessage={
                        generationProgress[generationProgress.length - 1] ||
                        'Generating syllabus...'
                      }
                      size="medium"
                    />
                  </Box>
                )}

                {generationError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {generationError}
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ mt: 2 }} />
          </Box>

          <form.Field
            name="text"
            children={(field) => (
              <TextField
                label="Syllabus Overview"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                rows={4}
                sx={modalInputSx}
                placeholder="Comprehensive overview of the course content and structure..."
              />
            )}
          />

          <form.Field
            name="practiceGap"
            children={(field) => (
              <TextField
                label="Practice Gap"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                rows={3}
                sx={modalInputSx}
                placeholder="Describe the gap in practice that this education addresses..."
              />
            )}
          />

          <form.Field
            name="practiceGapEvidence"
            children={(field) => (
              <TextField
                label="Practice Gap Evidence"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                rows={3}
                sx={modalInputSx}
                placeholder="Research and evidence supporting the identified practice gap..."
              />
            )}
          />

          <form.Field
            name="learnerEngagement"
            children={(field) => (
              <TextField
                label="Learner Engagement"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                rows={3}
                sx={modalInputSx}
                placeholder="How learners will engage with the content and participate..."
              />
            )}
          />

          <form.Field
            name="learningOutcomes"
            children={(field) => (
              <TextField
                label="Learning Outcomes"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                rows={3}
                sx={modalInputSx}
                placeholder="What learners will be able to do upon completion..."
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || aiGenerating}>
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading || aiGenerating}
        >
          {loading ? 'Creating...' : 'Create Syllabus'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
